"use client";

import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useSearchParams } from "next/navigation";
import formatNumber from "@/utils/formatNumber";
import Image from "next/image";
import NotFound from "@/assets/images/not-found.png";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import peopleIcon from "@/assets/icons/people-icon.webp";
import { Info } from "@phosphor-icons/react";
import CustomTooltip from "@/components/CustomTooltip";

export default function TotalPopulation({
  regionData,
  countryData,
  selectedCountryName,
  selectedRegionName,
}) {
  const searchParams = useSearchParams();
  const selectedCountryId = searchParams.get("country");

  // Generate chart data from World Bank API data
  const generateChartData = () => {
    if (regionData && Array.isArray(regionData)) {
      // Region view - show top 5 countries + Rest of Region
      const validCountries = regionData
        .filter((country) => country.macroeconomic?.population > 0)
        .sort(
          (a, b) =>
            (b.macroeconomic?.population || 0) -
            (a.macroeconomic?.population || 0),
        );

      const totalPopulation = validCountries.reduce(
        (sum, country) => sum + (country.macroeconomic?.population || 0),
        0,
      );

      const top5 = validCountries.slice(0, 5).map((country) => ({
        name: country.country_name || "Unknown",
        y: country.macroeconomic?.population || 0,
      }));

      const top5Sum = top5.reduce((sum, item) => sum + item.y, 0);
      const restSum = totalPopulation - top5Sum;
      const selectionName = selectedRegionName || "Region";

      if (restSum > 0) {
        top5.push({
          name: `Rest of ${selectionName}`,
          y: restSum,
          color: "#333333", // Neutral color
        });
      }

      return top5;
    } else {
      // No data available
      return [];
    }
  };

  const chartData = generateChartData();

  const chartColors = ["#00C49F", "#7E57C2", "#FFB300", "#29B6F6", "#FF7043"];

  // compute total dynamically from regionData if available to ensure accuracy
  const total =
    regionData && Array.isArray(regionData)
      ? regionData.reduce(
          (sum, c) => sum + (c.macroeconomic?.population || 0),
          0,
        )
      : chartData.reduce((sum, item) => sum + item.y, 0);

  const getTitle = () => {
    if (selectedCountryName) {
      return `Total Population — ${selectedCountryName}`;
    }
    return `Total Population — By Country`;
  };

  const options = {
    chart: {
      backgroundColor: "transparent",
      plotBackgroundColor: null,
      plotBorderWidth: 0,
      plotShadow: false,
      type: "pie",
      height: 300,
      style: {
        fontFamily: "inherit",
      },
    },
    title: {
      text:
        total > 0
          ? `<div style="text-align:center; display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100%"><span style="font-size:48px;font-weight:bold;color:#fff;line-height:1;display:block;margin-bottom:0px">${formatNumber(total, 1)}</span><span style="font-size:12px;opacity:0.6;color:#fff;display:block;max-width:180px;line-height:1.2;white-space:normal">${getTitle()}</span></div>`
          : null,
      align: "center",
      verticalAlign: "middle",
      y: 12,
      useHTML: true,
      style: {
        width: "180px",
      },
    },
    tooltip: {
      backgroundColor: "#161617",
      borderColor: "#333",
      style: { color: "#fff" },
      formatter: function () {
        return `<b>${this.point.name}</b><br/>
        Population: ${Highcharts.numberFormat(this.point.y, 0)}<br/>
        Share: ${Highcharts.numberFormat(this.point.percentage, 1)}%<br/>
        <span style="font-size: 10px; opacity: 0.6">Source: World Bank (Latest Year)</span>`;
      },
    },
    plotOptions: {
      pie: {
        innerSize: "80%", // doughnut
        borderColor: "#161617", // match chart background color
        borderWidth: 4, // creates visible gap
        dataLabels: { enabled: false },
        showInLegend: false,
      },
    },
    legend: { enabled: false },
    credits: { enabled: false },
    series: [
      {
        name: "Population",
        colorByPoint: true,
        colors: chartColors,
        data: chartData,
      },
    ],
  };

  return (
    <>
      {!selectedCountryId ? (
        <div>
          <div className='flex items-center justify-between mb-8'>
            <p className='text-base font-medium sm:text-sm'>{getTitle()}</p>
            <CustomTooltip
              trigger={
                <span className='-mt-2 -mr-2 text-white/40'>
                  <Info className='size-5' />
                </span>
              }
              content='Total population in the selected scope, sourced from the World Bank and using the latest available year (which may vary by country).'
            />
          </div>
          {chartData.length > 0 ? (
            <div className='relative'>
              {/* Center overlay */}
              <div className='-my-10'>
                <HighchartsReact highcharts={Highcharts} options={options} />
              </div>

              <div className='flex flex-wrap justify-center pb-6 mt-10 gap-x-3 gap-y-2'>
                {chartData.map((item, i) => (
                  <div
                    key={item.name}
                    className='inline-flex items-center gap-1.5 text-[10px]'
                  >
                    <span
                      className='rounded-full size-2'
                      style={{ backgroundColor: chartColors[i] }}
                    ></span>
                    <span className='opacity-60'>{item.name}</span>
                  </div>
                ))}
              </div>
              <div className='text-center mt-auto py-1 -mx-4 text-[10px] text-white/30 bg-white/[0.02] border-t border-white/5'>
                Source: World Bank
              </div>
            </div>
          ) : (
            <div className='p-6 py-4 my-4 text-center border border-dashed rounded-lg'>
              <div className='relative block mx-auto aspect-square size-20'>
                <Image
                  objectFit='cover'
                  src={NotFound.src}
                  alt='NotFound Image'
                  layout='fill'
                  className='object-cover'
                />
              </div>
              <p className='mt-3 text-base font-semibold'>Oops, No Data :(</p>
              <p className='mt-1 text-xs text-white/40'>
                No population data available
              </p>
            </div>
          )}
        </div>
      ) : (
        <div className='flex flex-col h-full'>
          <div className='flex items-center justify-between mb-2.5'>
            <p className='text-base font-medium sm:text-sm'>{getTitle()}</p>
          </div>
          <div className='flex items-center gap-3 pb-3.5'>
            <div className='relative size-9'>
              <Image
                src={peopleIcon.src}
                layout='fill'
                alt='Total Population'
              />
            </div>

            <span className='text-3xl font-bold'>
              {selectedCountryId && countryData?.macroeconomic?.population
                ? formatNumber(countryData.macroeconomic.population, 1)
                : "N/A"}
            </span>
          </div>
          <div className='text-center mt-auto py-1 -mx-4 text-[10px] text-white/30 bg-white/[0.02] border-t border-white/5'>
            Source: World Bank
          </div>
        </div>
      )}
    </>
  );
}
